import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { SiteNav } from './SiteNav';
import { useAuth, formatApiError } from '../contexts/AuthContext';
import { toast } from 'sonner';

const shellStyles = {
  wrap: { minHeight: 'calc(100vh - 64px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '64px 20px', background: '#F5F7FA' },
  card: { width: '100%', maxWidth: 440, background: '#fff', borderRadius: 14, padding: '36px 32px', boxShadow: '0 1px 3px rgba(15,39,66,.04), 0 4px 20px rgba(15,39,66,.06)' },
  eyebrow: { fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: '#64748B', marginBottom: 10 },
  h1: { fontSize: 24, fontWeight: 800, color: '#0F2742', margin: '0 0 10px', letterSpacing: '-0.01em' },
  sub: { fontSize: 14, color: '#64748B', margin: '0 0 28px', lineHeight: 1.55 },
  label: { display: 'block', fontSize: 11, fontWeight: 700, color: '#0F2742', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 },
  input: { width: '100%', padding: '12px 14px', border: '1.5px solid #E2E8F0', borderRadius: 10, fontSize: 14, color: '#0F2742', outline: 'none', fontFamily: 'inherit', marginBottom: 14 },
  btn: { width: '100%', padding: '14px 18px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', letterSpacing: '.01em' },
  btnDisabled: { opacity: 0.55, cursor: 'default' },
  err: { background: '#FEF2F2', color: '#991B1B', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14, border: '1px solid #FECACA' },
  ok: { background: '#F0FDF4', color: '#15803D', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14, border: '1px solid #BBF7D0' },
  hint: { fontSize: 13, color: '#64748B', marginTop: 18, textAlign: 'center' },
  link: { color: '#2563EB', textDecoration: 'none', fontWeight: 600 },
  check: { display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: '#475569', margin: '0 0 16px', cursor: 'pointer', lineHeight: 1.5 },
};

const Shell = ({ eyebrow, title, subtitle, children }) => (
  <div>
    <SiteNav cta="home" />
    <div style={shellStyles.wrap}>
      <div style={shellStyles.card}>
        {eyebrow && <div style={shellStyles.eyebrow}>{eyebrow}</div>}
        <h1 style={shellStyles.h1}>{title}</h1>
        {subtitle && <p style={shellStyles.sub}>{subtitle}</p>}
        {children}
      </div>
    </div>
  </div>
);

// ──────────────────────────────────────────────────────────────
// Login
// ──────────────────────────────────────────────────────────────
export const LoginPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const migrated = params.get('migrated') === '1';

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const u = await login({ email: email.trim().toLowerCase(), password });
      toast.success(`Welcome back`);
      const next = params.get('next');
      if (next && next.startsWith('/')) {
        navigate(next, { replace: true });
      } else {
        navigate(u?.tier ? '/account' : '/');
      }
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell eyebrow="Sign in" title="Sign in to your account" subtitle="Use the email address and password you registered with.">
      {migrated && (
        <div style={shellStyles.ok} data-testid="migrated-banner">
          Your password has been set. You can now sign in.
        </div>
      )}
      {err && <div style={shellStyles.err} data-testid="login-error">{err}</div>}
      <form onSubmit={submit} data-testid="login-form">
        <label style={shellStyles.label}>Email</label>
        <input style={shellStyles.input} type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} data-testid="login-email" />
        <label style={shellStyles.label}>Password</label>
        <input style={shellStyles.input} type="password" autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)} data-testid="login-password" />
        <button type="submit" style={busy ? { ...shellStyles.btn, ...shellStyles.btnDisabled } : shellStyles.btn} disabled={busy} data-testid="login-submit">
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      <div style={shellStyles.hint}>
        <Link to="/forgot-password" style={shellStyles.link} data-testid="forgot-link">Forgot your password?</Link>
      </div>
      <div style={shellStyles.hint}>
        New here? <Link to="/estimate" style={shellStyles.link}>Start a free estimate</Link> to create an account.
      </div>
    </Shell>
  );
};

// ──────────────────────────────────────────────────────────────
// Forgot password
// ──────────────────────────────────────────────────────────────
export const ForgotPasswordPage = () => {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await forgotPassword(email.trim().toLowerCase());
    } finally {
      setBusy(false);
      setSent(true);
    }
  };

  if (sent) {
    return (
      <Shell eyebrow="Check your email" title="Reset link sent" subtitle="If an account exists for this email, you'll receive a reset link shortly. It expires in 60 minutes.">
        <div style={shellStyles.ok} data-testid="forgot-sent">Email sent. Please check your inbox — and your spam folder if it doesn't appear within a few minutes.</div>
        <div style={shellStyles.hint}>
          <Link to="/login" style={shellStyles.link}>Back to sign in</Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell eyebrow="Reset password" title="Forgot your password?" subtitle="Enter your email and we'll send you a link to set a new one.">
      <form onSubmit={submit} data-testid="forgot-form">
        <label style={shellStyles.label}>Email</label>
        <input style={shellStyles.input} type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} data-testid="forgot-email" />
        <button type="submit" style={busy ? { ...shellStyles.btn, ...shellStyles.btnDisabled } : shellStyles.btn} disabled={busy} data-testid="forgot-submit">
          {busy ? 'Sending…' : 'Send reset link'}
        </button>
      </form>
      <div style={shellStyles.hint}>
        Remembered it? <Link to="/login" style={shellStyles.link}>Sign in</Link>
      </div>
    </Shell>
  );
};

// ──────────────────────────────────────────────────────────────
// Reset password (from email link)
// ──────────────────────────────────────────────────────────────
export const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { resetPassword } = useAuth();
  const token = params.get('token') || '';
  const [password, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (password.length < 8) { setErr('Password must be at least 8 characters'); return; }
    if (password !== confirm) { setErr('Passwords do not match'); return; }
    setBusy(true);
    try {
      await resetPassword({ token, password });
      toast.success('Password set — welcome!');
      navigate('/account');
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <Shell eyebrow="Link invalid" title="This reset link is missing a token" subtitle="Please request a new reset link.">
        <div style={shellStyles.hint}>
          <Link to="/forgot-password" style={shellStyles.link}>Request new link →</Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell eyebrow="Set password" title="Choose a new password" subtitle="Must be at least 8 characters. Use something you'll remember — there's no password hint.">
      {err && <div style={shellStyles.err} data-testid="reset-error">{err}</div>}
      <form onSubmit={submit} data-testid="reset-form">
        <label style={shellStyles.label}>New password</label>
        <input style={shellStyles.input} type="password" autoComplete="new-password" required minLength={8} value={password} onChange={e => setPw(e.target.value)} data-testid="reset-password" />
        <label style={shellStyles.label}>Confirm new password</label>
        <input style={shellStyles.input} type="password" autoComplete="new-password" required minLength={8} value={confirm} onChange={e => setConfirm(e.target.value)} data-testid="reset-confirm" />
        <button type="submit" style={busy ? { ...shellStyles.btn, ...shellStyles.btnDisabled } : shellStyles.btn} disabled={busy} data-testid="reset-submit">
          {busy ? 'Setting password…' : 'Set password and sign in'}
        </button>
      </form>
    </Shell>
  );
};
